import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { formatDate, formatDateTime, getAppTimeZone } from '@/lib/dates';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Loader2,
  Plus,
  RefreshCw,
  FileText,
  Eye,
  CalendarClock,
  CheckCircle2,
  ShieldCheck,
  Upload,
  X,
  Pencil,
  Trash2,
} from 'lucide-react';

type ApprovalStatus =
  | 'Current'
  | 'Active'
  | 'Upcoming'
  | 'Expired'
  | 'Open'
  | 'Inactive'
  | 'Unknown';

type TripFrequency = 'one_off' | 'weekly' | 'monthly' | 'open' | '';

interface ApprovalRow {
  id: string;
  account_id: string;
  account_name: string | null;
  approval_type: string | null;
  service_code: string | null;
  service_description: string | null;
  source_provider: string | null;
  claim_number: string | null;
  purchase_order_number: string | null;
  project: string;
  cost_code: string;
  reference: string;
  approval_from: string | null;
  approval_to: string | null;
  approval_is_open: boolean;
  trip_frequency: string | null;
  trip_count: number | null;
  has_document: boolean;
  original_file_name: string | null;
  stored_file_name: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
  mime_type: string | null;
  file_size_bytes: number | null;
  is_current: boolean;
  notes: string | null;
  is_active: boolean;
  uploaded_at: string | null;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  approval_status: ApprovalStatus;
  days_to_expiry: number | null;
}

interface CRMAccountApprovalsProps {
  accountId: string;
  accountName?: string | null;
  onCurrentChanged?: () => void | Promise<void>;
}

const BUCKET = 'crm-approval-docs';

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s_-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_');

const getStatusBadgeClass = (status: ApprovalStatus) => {
  switch (status) {
    case 'Current':
      return 'bg-green-900/50 text-green-400 border-green-700';
    case 'Active':
      return 'bg-emerald-900/40 text-emerald-400 border-emerald-700/50';
    case 'Open':
      return 'bg-blue-900/40 text-blue-400 border-blue-700/50';
    case 'Upcoming':
      return 'bg-yellow-900/40 text-yellow-400 border-yellow-700/50';
    case 'Expired':
      return 'bg-red-900/30 text-red-400 border-red-700/50';
    case 'Inactive':
      return 'bg-red-900/40 text-red-400 border-red-700/50';
    default:
      return 'bg-secondary text-muted-foreground border-border';
  }
};

const getEffectiveStatus = (row: {
  approval_status: ApprovalStatus;
  days_to_expiry: number | null;
  approval_is_open: boolean;
}): ApprovalStatus | 'Expiring' => {
  if (row.approval_is_open) return 'Open';
  if (row.days_to_expiry !== null && row.days_to_expiry !== undefined) {
    if (row.days_to_expiry < 0) return 'Expired';
    if (row.days_to_expiry <= 90) return 'Expiring' as any;
  }
  return row.approval_status;
};

const getEffectiveStatusClass = (status: ApprovalStatus | 'Expiring') => {
  if (status === 'Expiring') {
    return 'bg-amber-900/30 text-amber-400 border-amber-700/50';
  }
  return getStatusBadgeClass(status as ApprovalStatus);
};


const buildReference = (claimNumber: string, purchaseOrderNumber: string) => {
  const claim = claimNumber.trim();
  const po = purchaseOrderNumber.trim();

  if (claim && po) return `${claim} / ${po}`;
  if (claim) return claim;
  if (po) return po;
  return '';
};

const formatTripAllowance = (frequency?: string | null, count?: number | null) => {
  if (!frequency || frequency === 'open') {
    return 'Open / not specified';
  }

  if (!count || count <= 0) {
    if (frequency === 'one_off') return 'One-off';
    if (frequency === 'weekly') return 'Per week';
    if (frequency === 'monthly') return 'Per month';
    return '-';
  }

  if (frequency === 'one_off') {
    return `${count} ${count === 1 ? 'trip' : 'trips'} one-off`;
  }

  if (frequency === 'weekly') {
    return `${count} ${count === 1 ? 'trip' : 'trips'} / week`;
  }

  if (frequency === 'monthly') {
    return `${count} ${count === 1 ? 'trip' : 'trips'} / month`;
  }

  return '-';
};

const CRMAccountApprovals: React.FC<CRMAccountApprovalsProps> = ({
  accountId,
  accountName,
  onCurrentChanged,
}) => {
  const [approvals, setApprovals] = useState<ApprovalRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Unified create/edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const isEditing = editingId !== null;

  const [saving, setSaving] = useState(false);
  const [settingCurrentId, setSettingCurrentId] = useState<string | null>(null);

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);
  const [viewerRow, setViewerRow] = useState<ApprovalRow | null>(null);

  const [approvalType, setApprovalType] = useState('ACC');
  const [claimManager, setClaimManager] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [purchaseOrderNumber, setPurchaseOrderNumber] = useState('');
  const [serviceCode, setServiceCode] = useState('');
  const [serviceDescription, setServiceDescription] = useState('');
  const [tripFrequency, setTripFrequency] = useState<TripFrequency>('');
  const [tripCount, setTripCount] = useState('');
  const [project, setProject] = useState('');
  const [costCode, setCostCode] = useState('');
  const [reference, setReference] = useState('');
  const [approvalIsOpen, setApprovalIsOpen] = useState(false);
  const [approvalFrom, setApprovalFrom] = useState('');
  const [approvalTo, setApprovalTo] = useState('');
  const [notes, setNotes] = useState('');
  const [setAsCurrent, setSetAsCurrent] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Track existing document when editing
  const [existingDoc, setExistingDoc] = useState<{
    path: string | null;
    name: string | null;
    bucket: string | null;
  } | null>(null);
  const [removeExistingDoc, setRemoveExistingDoc] = useState(false);

  const currentApproval = useMemo(
    () => approvals.find((a) => a.is_current) ?? null,
    [approvals]
  );

  const loadApprovals = useCallback(
    async (mode: 'initial' | 'refresh' = 'initial') => {
      if (!accountId) return;

      if (mode === 'initial') setLoading(true);
      else setRefreshing(true);

      try {
        const { data, error } = await supabase
          .from('crm_account_approvals_with_status')
          .select('*')
          .eq('account_id', accountId)
          .order('approval_from', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false });

        if (error) throw error;
        setApprovals((data || []) as ApprovalRow[]);
      } catch (err: any) {
        console.error('Error loading approvals:', err);
        toast({
          title: 'Error',
          description: err.message || 'Failed to load approvals',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [accountId]
  );

  useEffect(() => {
    loadApprovals('initial');
  }, [loadApprovals]);

  const resetForm = () => {
    setEditingId(null);
    setApprovalType('ACC');
    setClaimManager('');
    setClaimNumber('');
    setPurchaseOrderNumber('');
    setServiceCode('');
    setServiceDescription('');
    setTripFrequency('');
    setTripCount('');
    setProject('');
    setCostCode('');
    setReference('');
    setApprovalIsOpen(false);
    setApprovalFrom('');
    setApprovalTo('');
    setNotes('');
    setSetAsCurrent(false);
    setSelectedFile(null);
    setExistingDoc(null);
    setRemoveExistingDoc(false);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (row: ApprovalRow) => {
    setEditingId(row.id);
    setApprovalType(row.approval_type || '');
    setClaimManager(row.source_provider || '');
    setClaimNumber(row.claim_number || '');
    setPurchaseOrderNumber(row.purchase_order_number || '');
    setServiceCode(row.service_code || '');
    setServiceDescription(row.service_description || '');
    setTripFrequency((row.trip_frequency as TripFrequency) || '');
    setTripCount(row.trip_count != null ? String(row.trip_count) : '');
    setProject(row.project || '');
    setCostCode(row.cost_code || '');
    setReference(row.reference || '');
    setApprovalIsOpen(!!row.approval_is_open);
    setApprovalFrom(row.approval_from || '');
    setApprovalTo(row.approval_to || '');
    setNotes(row.notes || '');
    setSetAsCurrent(!!row.is_current);
    setSelectedFile(null);
    setExistingDoc(
      row.has_document
        ? {
            path: row.storage_path,
            name: row.original_file_name,
            bucket: row.storage_bucket || BUCKET,
          }
        : null
    );
    setRemoveExistingDoc(false);
    setDialogOpen(true);
  };

  const buildStoragePath = (file: File) => {
    const now = new Date();
    const nzParts = new Intl.DateTimeFormat('en-NZ', {
      timeZone: getAppTimeZone(),
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(now);

    const get = (type: string) => nzParts.find((p) => p.type === type)?.value || '';
    const yyyy = get('year');
    const mm = get('month');
    const dd = get('day');
    const hh = get('hour');
    const mi = get('minute');
    const ss = get('second');

    const ext = file.name.includes('.')
      ? file.name.split('.').pop()?.toLowerCase() || 'file'
      : 'file';

    const shortName = slugify(accountName || accountId || 'account');
    const fileName = `${accountId}_${shortName}_${yyyy}${mm}${dd}_${hh}${mi}${ss}_${crypto
      .randomUUID()
      .slice(0, 6)}.${ext}`;

    return {
      storedFileName: fileName,
      storagePath: `${accountId}/${yyyy}/${mm}/${fileName}`,
    };
  };

  const handleServiceCodeChange = (value: string) => {
    setServiceCode(value);
    setCostCode(value);
  };

  const handleClaimNumberChange = (value: string) => {
    setClaimNumber(value);
    setReference(buildReference(value, purchaseOrderNumber));
  };

  const handlePurchaseOrderNumberChange = (value: string) => {
    setPurchaseOrderNumber(value);
    setReference(buildReference(claimNumber, value));
  };

  const handleClaimManagerChange = (value: string) => {
    setClaimManager(value);
    setProject(value);
  };

  const handleSaveApproval = async () => {
    if (!accountId) return;

    if (!project.trim() || !costCode.trim() || !reference.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'Project, Cost Code, and Reference are required.',
        variant: 'destructive',
      });
      return;
    }

    if (!approvalIsOpen && (!approvalFrom || !approvalTo)) {
      toast({
        title: 'Missing approval dates',
        description: 'Please set both approval start and end dates, or tick open approval.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    try {
      // Determine document metadata changes
      let fileMeta: Record<string, any> | null = null;
      let oldPathToRemove: string | null = null;

      if (selectedFile) {
        // Uploading a replacement / new document
        const { storedFileName, storagePath } = buildStoragePath(selectedFile);

        const { error: uploadError } = await supabase.storage
          .from(BUCKET)
          .upload(storagePath, selectedFile, {
            upsert: false,
            contentType: selectedFile.type || undefined,
          });

        if (uploadError) throw uploadError;

        fileMeta = {
          has_document: true,
          original_file_name: selectedFile.name,
          stored_file_name: storedFileName,
          storage_bucket: BUCKET,
          storage_path: storagePath,
          mime_type: selectedFile.type || null,
          file_size_bytes: selectedFile.size,
          uploaded_at: new Date().toISOString(),
        };

        if (isEditing && existingDoc?.path) {
          oldPathToRemove = existingDoc.path;
        }
      } else if (isEditing && removeExistingDoc && existingDoc?.path) {
        // User explicitly removed the existing doc without replacing it
        fileMeta = {
          has_document: false,
          original_file_name: null,
          stored_file_name: null,
          storage_bucket: BUCKET,
          storage_path: null,
          mime_type: null,
          file_size_bytes: null,
          uploaded_at: null,
        };
        oldPathToRemove = existingDoc.path;
      } else if (!isEditing) {
        // Creating without a document
        fileMeta = {
          has_document: false,
          original_file_name: null,
          stored_file_name: null,
          storage_bucket: BUCKET,
          storage_path: null,
          mime_type: null,
          file_size_bytes: null,
          uploaded_at: null,
        };
      }

      const parsedTripCount =
        tripCount.trim() === '' ? null : Number.parseInt(tripCount, 10);

      const baseFields = {
        account_name: accountName || null,
        approval_type: approvalType || null,
        service_code: serviceCode || null,
        service_description: serviceDescription || null,
        source_provider: claimManager || null,
        claim_number: claimNumber || null,
        purchase_order_number: purchaseOrderNumber || null,
        trip_frequency: tripFrequency || null,
        trip_count: Number.isNaN(parsedTripCount) ? null : parsedTripCount,
        project: project.trim(),
        cost_code: costCode.trim(),
        reference: reference.trim(),
        approval_from: approvalIsOpen ? null : approvalFrom,
        approval_to: approvalIsOpen ? null : approvalTo,
        approval_is_open: approvalIsOpen,
        notes: notes || null,
      };

      let savedId: string | null = null;

      if (isEditing && editingId) {
        // UPDATE existing approval
        const updatePayload: Record<string, any> = { ...baseFields };
        if (fileMeta) Object.assign(updatePayload, fileMeta);

        const { error: updateError } = await supabase
          .from('crm_account_approvals')
          .update(updatePayload)
          .eq('id', editingId);

        if (updateError) throw updateError;
        savedId = editingId;
      } else {
        // INSERT new approval
        const insertPayload: Record<string, any> = {
          account_id: accountId,
          ...baseFields,
          ...(fileMeta || {}),
        };

        const { data: inserted, error: insertError } = await supabase
          .from('crm_account_approvals')
          .insert(insertPayload)
          .select('id')
          .single();

        if (insertError) throw insertError;
        savedId = inserted?.id || null;
      }

      // Remove the old file AFTER the DB update succeeds
      if (oldPathToRemove) {
        const { error: removeError } = await supabase.storage
          .from(BUCKET)
          .remove([oldPathToRemove]);
        if (removeError) {
          // Non-fatal — just warn
          console.warn('Failed to remove old document:', removeError);
        }
      }

      // Handle "Set as Current" toggle
      if (savedId) {
        if (isEditing) {
          // When editing, only call the RPC if the user enabled Set as Current
          // AND this row isn't already current
          const wasAlreadyCurrent = approvals.find((a) => a.id === savedId)?.is_current;
          if (setAsCurrent && !wasAlreadyCurrent) {
            const { error: rpcError } = await supabase.rpc('crm_set_approval_as_current', {
              p_approval_id: savedId,
            });
            if (rpcError) throw rpcError;
          }
        } else if (setAsCurrent) {
          const { error: rpcError } = await supabase.rpc('crm_set_approval_as_current', {
            p_approval_id: savedId,
          });
          if (rpcError) throw rpcError;
        }
      }

      toast({
        title: isEditing ? 'Approval updated' : 'Approval added',
        description: isEditing
          ? 'Changes saved successfully.'
          : setAsCurrent
          ? 'Approval saved and set as current.'
          : 'Approval saved successfully.',
      });

      setDialogOpen(false);
      resetForm();
      await loadApprovals('refresh');
      await onCurrentChanged?.();
    } catch (err: any) {
      console.error('Error saving approval:', err);
      toast({
        title: 'Save failed',
        description: err.message || 'Could not save approval',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSetAsCurrent = async (approvalId: string) => {
    setSettingCurrentId(approvalId);
    try {
      const { error } = await supabase.rpc('crm_set_approval_as_current', {
        p_approval_id: approvalId,
      });

      if (error) throw error;

      toast({
        title: 'Current approval updated',
        description:
          'This approval is now the live Project / Cost Code / Reference for the account.',
      });

      await loadApprovals('refresh');
      await onCurrentChanged?.();
    } catch (err: any) {
      console.error('Error setting current approval:', err);
      toast({
        title: 'Update failed',
        description: err.message || 'Could not set approval as current',
        variant: 'destructive',
      });
    } finally {
      setSettingCurrentId(null);
    }
  };

  const handleViewDocument = async (row: ApprovalRow) => {
    if (!row.storage_path) return;

    setViewerRow(row);
    setViewerOpen(true);
    setViewerLoading(true);
    setViewerUrl(null);

    try {
      const { data, error } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(row.storage_path, 60 * 10);

      if (error) throw error;

      setViewerUrl(data.signedUrl);
    } catch (err: any) {
      console.error('Error opening approval document:', err);
      toast({
        title: 'Could not open document',
        description: err.message || 'Failed to generate document link',
        variant: 'destructive',
      });
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  return (
    <>
      <Card className="bg-secondary/30 border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wide flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Approvals
              <Badge variant="secondary" className="text-xs">
                {approvals.length}
              </Badge>
              {currentApproval && (
                <Badge className="bg-green-900/50 text-green-400 border-green-700 text-xs">
                  Current Set
                </Badge>
              )}
            </CardTitle>

            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                className="text-muted-foreground hover:text-foreground"
                onClick={() => loadApprovals('refresh')}
                disabled={refreshing}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>

              <Button
                size="sm"
                className="golden-button"
                onClick={openCreateDialog}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Add Approval
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {currentApproval && (() => {
            const curEffective = getEffectiveStatus(currentApproval);
            return (
              <div className={`mb-4 rounded-lg border p-3 ${
                curEffective === 'Expired'
                  ? 'border-red-700/40 bg-red-900/10'
                  : curEffective === 'Expiring'
                  ? 'border-amber-700/40 bg-amber-900/10'
                  : 'border-green-700/40 bg-green-900/10'
              }`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-sm font-medium text-foreground flex items-center gap-2">
                      <CheckCircle2 className={`w-4 h-4 ${
                        curEffective === 'Expired'
                          ? 'text-red-400'
                          : curEffective === 'Expiring'
                          ? 'text-amber-400'
                          : 'text-green-400'
                      }`} />
                      Current Approval
                      {curEffective === 'Expired' && (
                        <span className="text-[10px] font-normal text-red-400/80">
                          · needs renewing
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {currentApproval.project} • {currentApproval.cost_code} • {currentApproval.reference}
                    </p>
                  </div>
                  <Badge className={getEffectiveStatusClass(curEffective)}>
                    {curEffective}
                  </Badge>
                </div>
              </div>
            );
          })()}

          {loading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading approvals...
            </div>
          ) : approvals.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              No approvals recorded for this account yet.
            </div>
          ) : (
            <div className="space-y-3 max-h-[560px] overflow-y-auto pr-1">
              {approvals.map((row) => {
                const effectiveStatus = getEffectiveStatus(row);
                const isExpired = effectiveStatus === 'Expired';
                const isCurrent = row.is_current;

                return (
                  <div
                    key={row.id}
                    className={`rounded-xl border p-4 transition-colors ${
                      isExpired && !isCurrent
                        ? 'border-border bg-secondary/20 opacity-65'
                        : isExpired && isCurrent
                        ? 'border-red-700/40 bg-red-900/10'
                        : isCurrent
                        ? 'border-green-700/40 bg-green-900/10'
                        : 'border-border bg-secondary/20'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground">
                            {row.approval_type || 'Approval'}
                          </p>
                          {row.service_code && (
                            <Badge variant="outline" className="text-[10px]">
                              {row.service_code}
                            </Badge>
                          )}
                          <Badge className={getEffectiveStatusClass(effectiveStatus)}>
                            {effectiveStatus}
                          </Badge>
                          {row.is_current && (
                            <Badge className={`text-[10px] ${
                              isExpired
                                ? 'bg-red-900/40 text-red-400 border-red-700/50'
                                : 'bg-green-900/50 text-green-400 border-green-700'
                            }`}>
                              Current{isExpired ? ' (Expired)' : ''}
                            </Badge>
                          )}
                        </div>


                        <p className="text-xs text-muted-foreground mt-1">
                          {row.project} • {row.cost_code} • {row.reference}
                        </p>

                        {row.source_provider && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Claim Manager: {row.source_provider}
                          </p>
                        )}

                        {row.service_description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {row.service_description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {row.has_document && row.storage_path && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-600/60 text-blue-400 hover:bg-blue-900/30"
                            onClick={() => handleViewDocument(row)}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1.5" />
                            View Doc
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="outline"
                          className="border-amber-600/60 text-amber-400 hover:bg-amber-900/30"
                          onClick={() => openEditDialog(row)}
                        >
                          <Pencil className="w-3.5 h-3.5 mr-1.5" />
                          Edit
                        </Button>

                        {!row.is_current && row.is_active && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-green-600/60 text-green-400 hover:bg-green-900/30"
                            onClick={() => handleSetAsCurrent(row.id)}
                            disabled={settingCurrentId === row.id}
                          >
                            {settingCurrentId === row.id ? (
                              <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                            ) : (
                              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />
                            )}
                            Set as Current
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-3 text-xs">
                      <div>
                        <p className="text-muted-foreground">Approval Period</p>
                        <p className="text-foreground">
                          {row.approval_is_open
                            ? 'Open Approval'
                            : `${formatDate(row.approval_from)} → ${formatDate(row.approval_to)}`}
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Claim / PO</p>
                        <p className="text-foreground">
                          {[row.claim_number, row.purchase_order_number]
                            .filter(Boolean)
                            .join(' • ') || '-'}
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Trip Allowance</p>
                        <p className="text-foreground">
                          {formatTripAllowance(row.trip_frequency, row.trip_count)}
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Uploaded</p>
                        <p className="text-foreground">
                          {formatDateTime(row.uploaded_at || row.created_at)}
                        </p>
                      </div>

                      <div>
                        <p className="text-muted-foreground">Document</p>
                        <p className="text-foreground flex items-center gap-1">
                          {row.has_document ? (
                            <>
                              <FileText className="w-3.5 h-3.5 text-blue-400" />
                              {row.original_file_name || 'Attached'}
                            </>
                          ) : (
                            '-'
                          )}
                        </p>
                      </div>
                    </div>

                    {row.notes && (
                      <div className="mt-3 rounded-lg bg-secondary/40 border border-border p-3">
                        <p className="text-[11px] text-muted-foreground mb-1">Notes</p>
                        <p className="text-sm text-foreground whitespace-pre-wrap">{row.notes}</p>
                      </div>
                    )}

                    {isExpired && (
                      <div className="mt-3 flex items-center gap-2 text-[11px] text-muted-foreground">
                        <CalendarClock className="w-3.5 h-3.5" />
                        This approval has expired but remains available for viewing.
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}
      >
        <DialogContent className="max-w-3xl bg-card border-border max-h-[90vh] overflow-y-auto">
          <DialogHeader className="flex flex-row items-start justify-between space-y-0 border-b border-border pb-4">
            <DialogTitle className="golden-text">
              {isEditing ? 'Edit Approval' : 'Add Approval'}
            </DialogTitle>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
              onClick={() => setDialogOpen(false)}
            >
              <X className="w-4 h-4" />
            </Button>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div className="space-y-2">
              <Label>Approval Type</Label>
              <Input
                value={approvalType}
                onChange={(e) => setApprovalType(e.target.value)}
                placeholder="ACC"
              />
            </div>

            <div className="space-y-2">
              <Label>Claim Manager</Label>
              <Input
                value={claimManager}
                onChange={(e) => handleClaimManagerChange(e.target.value)}
                placeholder="CM Viv Richard"
              />
            </div>

            <div className="space-y-2">
              <Label>Claim Number</Label>
              <Input
                value={claimNumber}
                onChange={(e) => handleClaimNumberChange(e.target.value)}
                placeholder="90541232"
              />
            </div>

            <div className="space-y-2">
              <Label>Purchase Order Number</Label>
              <Input
                value={purchaseOrderNumber}
                onChange={(e) => handlePurchaseOrderNumberChange(e.target.value)}
                placeholder="10110384"
              />
            </div>

            <div className="space-y-2">
              <Label>Service Code</Label>
              <Input
                value={serviceCode}
                onChange={(e) => handleServiceCodeChange(e.target.value)}
                placeholder="TRI01"
              />
            </div>

            <div className="space-y-2">
              <Label>Service Description</Label>
              <Input
                value={serviceDescription}
                onChange={(e) => setServiceDescription(e.target.value)}
                placeholder=""
              />
            </div>

            <div className="space-y-2">
              <Label>Trip Frequency</Label>
              <select
                value={tripFrequency}
                onChange={(e) => setTripFrequency(e.target.value as TripFrequency)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
              >
                <option value="">Select frequency</option>
                <option value="one_off">One Off</option>
                <option value="weekly">Per Week</option>
                <option value="monthly">Per Month</option>
                <option value="open">Open / Not Specified</option>
              </select>
            </div>

            <div className="space-y-2">
              <Label>Trip Count</Label>
              <Input
                type="number"
                min="0"
                value={tripCount}
                onChange={(e) => setTripCount(e.target.value)}
                placeholder="e.g. 2"
              />
            </div>

            <div className="space-y-2">
              <Label>Project *</Label>
              <Input
                value={project}
                onChange={(e) => setProject(e.target.value)}
                placeholder="Auto-filled from Claim Manager"
              />
            </div>

            <div className="space-y-2">
              <Label>Cost Code *</Label>
              <Input
                value={costCode}
                onChange={(e) => setCostCode(e.target.value)}
                placeholder="Auto-filled from Service Code"
              />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Reference *</Label>
              <Input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                placeholder="Auto-filled from Claim Number / Purchase Order Number"
              />
            </div>

            <div className="md:col-span-2 rounded-lg border border-border bg-secondary/20 p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-foreground">Open Approval</p>
                  <p className="text-xs text-muted-foreground">
                    Use this when there is no fixed start/end date.
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={approvalIsOpen ? 'default' : 'outline'}
                  className={approvalIsOpen ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}
                  onClick={() => setApprovalIsOpen((prev) => !prev)}
                >
                  {approvalIsOpen ? 'Yes' : 'No'}
                </Button>
              </div>

              {!approvalIsOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="space-y-2">
                    <Label>Approval From *</Label>
                    <Input
                      type="date"
                      value={approvalFrom}
                      onChange={(e) => setApprovalFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Approval To *</Label>
                    <Input
                      type="date"
                      value={approvalTo}
                      onChange={(e) => setApprovalTo(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>{isEditing ? 'Document' : 'Attach Document'}</Label>

              {isEditing && existingDoc?.path && !removeExistingDoc && !selectedFile && (
                <div className="rounded-lg border border-border bg-secondary/30 p-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className="w-4 h-4 text-blue-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-foreground truncate">
                        {existingDoc.name || 'Attached document'}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        Currently attached. Upload a new file below to replace it, or remove it.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-red-700/50 text-red-400 hover:bg-red-900/30"
                    onClick={() => setRemoveExistingDoc(true)}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Remove
                  </Button>
                </div>
              )}

              {isEditing && removeExistingDoc && !selectedFile && (
                <div className="rounded-lg border border-red-700/40 bg-red-900/10 p-3 flex items-center justify-between gap-3 flex-wrap">
                  <p className="text-sm text-red-300">
                    The attached document will be removed when you save.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-border text-muted-foreground"
                    onClick={() => setRemoveExistingDoc(false)}
                  >
                    Undo
                  </Button>
                </div>
              )}

              <Input
                type="file"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setSelectedFile(f);
                  if (f && isEditing) {
                    // Replacing old doc implies removing it
                    setRemoveExistingDoc(false);
                  }
                }}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp"
              />

              {selectedFile && (
                <p className="text-[11px] text-blue-400">
                  {isEditing && existingDoc?.path
                    ? `Will replace the current document with: ${selectedFile.name}`
                    : `Selected: ${selectedFile.name}`}
                </p>
              )}

              <p className="text-[11px] text-muted-foreground">
                File will be auto-named and stored in the account folder inside the approval bucket.
              </p>
            </div>

            <div className="md:col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
                rows={4}
              />
            </div>

            <div className="md:col-span-2 rounded-lg border border-green-700/30 bg-green-900/10 p-3">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-medium text-foreground">Set as Current</p>
                  <p className="text-xs text-muted-foreground">
                    {isEditing
                      ? "If turned on, this approval will be pushed into the account's live Project / Cost Code / Reference."
                      : "Push this approval into the account's live Project / Cost Code / Reference."}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={setAsCurrent ? 'default' : 'outline'}
                  className={setAsCurrent ? 'bg-green-600 hover:bg-green-700 text-white' : ''}
                  onClick={() => setSetAsCurrent((prev) => !prev)}
                >
                  {setAsCurrent ? 'Yes' : 'No'}
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              className="border-border text-muted-foreground"
              onClick={() => setDialogOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              className="golden-button"
              onClick={handleSaveApproval}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-1.5" />
              )}
              {isEditing ? 'Save Changes' : 'Save Approval'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={viewerOpen} onOpenChange={setViewerOpen}>
        <DialogContent className="max-w-5xl h-[85vh] bg-card border-border p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-border">
            <DialogTitle className="golden-text flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {viewerRow?.original_file_name || 'Approval Document'}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 h-full">
            {viewerLoading ? (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Loading document...
              </div>
            ) : viewerUrl ? (
              <iframe
                src={viewerUrl}
                title="Approval Document"
                className="w-full h-[calc(85vh-72px)] border-0"
              />
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                Could not load document preview.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CRMAccountApprovals;
